/**
 * Inhalt eines Behälters als Zeilen -- für die Behälter-Seite und den
 * Lager-Baum (ADR 0013 §2, §13).
 *
 * Gleiche Artikel stehen zusammengefasst („5× Leitung CEE 125 A“, aufklappbar
 * zu den Nummern), Fremdes ist markiert („gehört nach GB-03“), und was fehlt,
 * steht gestrichelt mit dem Ort, an dem es gerade ist.
 */

import { useState } from "react";
import { Link } from "react-router";
import type { Artikel, Lagerort } from "@thw/contracts";
import type { Gruppe } from "../lokal/behaelter.js";
import type { Bestand } from "../lokal/bestand.js";
import { fehltText, stammplatzVon } from "../lokal/stammplatz.js";
import { woWar } from "../lokal/verschieben.js";
import { useStatusArten } from "../lokal/bestand.js";
import { Farbpunkt, Nummer } from "./bausteine.jsx";

/**
 * Der Status als kleiner Farbpunkt (Rückmeldungen 3.2). Der Name steht nur
 * dabei, wenn es nicht der Standard ist -- Abweichungen fallen so auf, und
 * wer Farben schlecht unterscheidet, liest sie trotzdem.
 */
function StatusKlein({ status }: { status: string }) {
  const art = useStatusArten().find((a) => a.schluessel === status);
  const name = art?.bezeichnung ?? status;
  return (
    <span className="inline-flex items-center gap-1.5" title={name}>
      <Farbpunkt farbe={art?.farbe ?? "#a3a3a3"} />
      {art?.standard === true ? (
        <span className="sr-only">{name}</span>
      ) : (
        <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{name}</span>
      )}
    </span>
  );
}

/** Ein Lagerort als kurzer Name: „T7-F1“, „Ohne festen Platz“. */
export function kurz(l: Lagerort | undefined): string {
  if (l === undefined) return "unbekannter Lagerort";
  return l.standardziel ? l.bezeichnung : l.kennung;
}

/** „vollständig“ oder „3 fehlen“ als Schild an einer Zeile. */
export function FehltSchild({ fehlt }: { fehlt: number }) {
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        fehlt === 0
          ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
          : "bg-amber-400 text-neutral-900"
      }`}
    >
      {fehltText(fehlt)}
    </span>
  );
}

/** „gehört nach GB-03“ -- für Fremdes, das hier liegt (ADR 0013 §9). */
export function GehoertNach({
  bestand,
  artikel_id,
  hier,
}: {
  bestand: Bestand;
  artikel_id: string;
  hier: string;
}) {
  const stammplatz = stammplatzVon(bestand, artikel_id);
  if (stammplatz === null || stammplatz === hier) return null;
  return (
    <span className="rounded-full bg-amber-400 px-2.5 py-0.5 text-xs font-semibold text-neutral-900">
      gehört nach {kurz(bestand.lagerorte.get(stammplatz))}
    </span>
  );
}

/**
 * Eine Zeile Inhalt. Ein einzelner Artikel führt direkt zu ihm; gleiche
 * Artikel stehen als „5× Leitung CEE 125 A“ und klappen zu ihren Nummern auf.
 * Fremdes ist markiert: „gehört nach GB-03“.
 */
export function GruppenZeile({
  gruppe,
  bestand,
  hier,
}: {
  gruppe: Gruppe;
  bestand: Bestand;
  hier: string;
}) {
  const [offen, setOffen] = useState(false);
  const einzeln = gruppe.eintraege.length === 1 ? gruppe.eintraege[0] : undefined;

  if (einzeln !== undefined) {
    return (
      <Link
        to={`/artikel/${einzeln.artikel.id}`}
        className="flex min-h-14 flex-col justify-center gap-1 rounded-xl border border-neutral-300 bg-white px-3 py-2 dark:border-neutral-600 active:bg-neutral-100 dark:bg-neutral-800"
      >
        <span className="font-semibold">{einzeln.artikel.bezeichnung}</span>
        <span className="flex flex-wrap items-center gap-3">
          <Nummer>{einzeln.artikel.inventarnummer}</Nummer>
          <StatusKlein status={einzeln.zustand.status} />
          <GehoertNach bestand={bestand} artikel_id={einzeln.artikel.id} hier={hier} />
        </span>
      </Link>
    );
  }

  const fremd = gruppe.eintraege.filter((e) => {
    const s = stammplatzVon(bestand, e.artikel.id);
    return s !== null && s !== hier;
  }).length;

  return (
    <div className="rounded-xl border border-neutral-300 bg-white dark:border-neutral-600 dark:bg-neutral-800">
      <button
        type="button"
        aria-expanded={offen}
        onClick={() => {
          setOffen(!offen);
        }}
        className="flex min-h-14 w-full items-center justify-between gap-2 p-3 text-left"
      >
        <span className="font-semibold">
          {gruppe.eintraege.length}× {gruppe.bezeichnung}
          {fremd > 0 && (
            <span className="ml-2 text-sm font-normal text-amber-800 dark:text-amber-300">
              davon {fremd} fremd
            </span>
          )}
        </span>
        <span aria-hidden className="text-xl">
          {offen ? "▴" : "▾"}
        </span>
      </button>
      {offen && (
        <ul className="flex flex-col border-t border-neutral-200 dark:border-neutral-700">
          {gruppe.eintraege.map((e) => (
            <li key={e.artikel.id}>
              <Link
                to={`/artikel/${e.artikel.id}`}
                className="flex min-h-12 flex-wrap items-center justify-between gap-3 px-3 active:bg-neutral-100 dark:active:bg-neutral-700"
              >
                <Nummer>{e.artikel.inventarnummer}</Nummer>
                <span className="flex items-center gap-2">
                  <GehoertNach bestand={bestand} artikel_id={e.artikel.id} hier={hier} />
                  <StatusKlein status={e.zustand.status} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Was hier seinen Stammplatz hat, aber nicht hier ist -- und wo es gerade ist. */
export function FehltListe({ bestand, fehlend }: { bestand: Bestand; fehlend: readonly string[] }) {
  const eintraege = fehlend
    .map((id) => bestand.artikel.get(id))
    .filter((a): a is Artikel => a !== undefined)
    .sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung, "de"));
  return (
    <ul className="flex flex-col gap-2">
      {eintraege.map((artikel) => {
        const zustand = bestand.zustaende.get(artikel.id);
        return (
          <li key={artikel.id}>
            <Link
              to={`/artikel/${artikel.id}`}
              className="flex min-h-14 flex-col justify-center gap-1 rounded-xl border-2 border-dashed border-amber-500 bg-white p-3 active:bg-neutral-100 dark:bg-neutral-800"
            >
              <span className="font-semibold">{artikel.bezeichnung}</span>
              <span className="flex flex-wrap items-center gap-3 text-sm text-neutral-600 dark:text-neutral-300">
                <Nummer>{artikel.inventarnummer}</Nummer>
                <span>
                  {zustand === undefined ? "noch nicht erfasst" : woWar(bestand, zustand)}
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
